import axios from "axios";
import { useMutation, useQuery, useQueryClient } from "react-query";
import toast from "react-hot-toast";
import { useAdminAuth } from "./useAdminAuth";
import type { Release } from "../types";

const adminApi = axios.create({ baseURL: "/api/admin" });

export interface AdminReleasesResponse {
  data: Release[];
  meta: { page: number; limit: number; total: number; totalPages: number };
}

export type AiProviderType = "openai-compatible" | "anthropic";

export interface AiProviderReasoningConfig {
  enabled: boolean;
  effort: "low" | "medium" | "high" | "max";
}

export interface AiProviderConfig {
  id: string;
  name: string;
  type: AiProviderType;
  baseURL?: string;
  apiKey: string;
  model: string;
  enabled: boolean;
  priority: number;
  reasoning?: AiProviderReasoningConfig;
  supportsStructuredOutputs?: boolean;
  pricePerMTokenIn?: number;
  pricePerMTokenOut?: number;
}

export type AiProviderPublic = Omit<AiProviderConfig, "apiKey"> & {
  hasApiKey: boolean;
  apiKeyPreview: string;
};

export interface AiConfigResponse {
  providers: AiProviderPublic[];
}

export interface AiModel {
  name: string;
  displayName: string;
  description: string;
  inputTokenLimit?: number;
  outputTokenLimit?: number;
  reasoning?: boolean;
  recommended?: boolean;
}

const headers = (token: string | null) => ({
  Authorization: token ? `Bearer ${token}` : "",
});

export const useAdminReleases = (page: number, limit: number, search: string) => {
  const { token } = useAdminAuth();
  return useQuery<AdminReleasesResponse>(
    ["admin-releases", page, limit, search],
    async () => {
      const { data } = await adminApi.get<{ data: Release[]; meta: AdminReleasesResponse["meta"] }>(
        "/releases",
        {
          params: { page, limit, search: search || undefined },
          headers: headers(token),
        }
      );
      return { data: data.data, meta: data.meta };
    },
    { keepPreviousData: true, enabled: !!token }
  );
};

export const useUpdateRelease = () => {
  const { token } = useAdminAuth();
  const queryClient = useQueryClient();
  return useMutation<
    Release,
    Error,
    { id: string; summary?: string; tagName?: string; releaseDate?: string }
  >(
    async ({ id, ...payload }) => {
      const { data } = await adminApi.patch<{ data: Release }>(`/release/${id}`, payload, {
        headers: headers(token),
      });
      return data.data;
    },
    {
      onSuccess: () => {
        toast.success("Release atualizada");
        queryClient.invalidateQueries("admin-releases");
        queryClient.invalidateQueries("changelog");
        queryClient.invalidateQueries("stats");
      },
      onError: () => {
        toast.error("Falha ao atualizar release");
      },
    }
  );
};

export const useDeleteRelease = () => {
  const { token } = useAdminAuth();
  const queryClient = useQueryClient();
  return useMutation<{ id: string; tagName: string }, Error, string>(
    async (id) => {
      const { data } = await adminApi.delete<{ data: { id: string; tagName: string } }>(
        `/release/${id}`,
        { headers: headers(token) }
      );
      return data.data;
    },
    {
      onSuccess: (deleted) => {
        toast.success(`Release ${deleted.tagName} removida`);
        queryClient.invalidateQueries("admin-releases");
        queryClient.invalidateQueries("changelog");
        queryClient.invalidateQueries("stats");
      },
      onError: () => {
        toast.error("Falha ao remover release");
      },
    }
  );
};

export const useAiConfig = () => {
  const { token } = useAdminAuth();
  return useQuery<AiConfigResponse>(
    "admin-ai-config",
    async () => {
      const res = await adminApi.get<{ data: AiConfigResponse }>("/ai-config", {
        headers: headers(token),
      });
      return res.data.data;
    },
    { enabled: !!token }
  );
};

export const useUpdateAiConfig = () => {
  const { token } = useAdminAuth();
  const queryClient = useQueryClient();
  return useMutation<void, Error, { providers: AiProviderConfig[] }>(
    async (payload) => {
      await adminApi.post("/ai-config", payload, { headers: headers(token) });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries("admin-ai-config");
        toast.success("Configuração atualizada");
      },
    }
  );
};

export const useFetchModels = () => {
  const { token } = useAdminAuth();
  return useMutation<
    AiModel[],
    Error,
    { id?: string; type: AiProviderType; baseURL?: string; apiKey: string }
  >(
    async (params) => {
      const res = await adminApi.post<{ data: { models: AiModel[] } }>(
        "/models",
        params,
        { headers: headers(token) }
      );
      return res.data.data.models;
    },
    {
      onError: () => {
        toast.error("Falha ao buscar modelos do provider");
      },
    }
  );
};

export type StructuredOutputsSupport = "supported" | "rejected" | "unknown";

export type ReasoningSupport = "used" | "accepted" | "rejected" | "unknown";

export type ReasoningDelivery = "separate" | "inline" | "unknown";

export type EffortLevel = "low" | "medium" | "high" | "max";

export interface ProviderCapabilities {
  structuredOutputs: StructuredOutputsSupport;
  reasoning: ReasoningSupport;
  reasoningTokens?: number;
  maxEffort?: EffortLevel;
  reasoningDelivery: ReasoningDelivery;
}

export interface TestProviderResult {
  ok: boolean;
  latencyMs: number;
  error?: string;
  capabilities?: ProviderCapabilities;
}

export const useTestProvider = () => {
  const { token } = useAdminAuth();
  return useMutation<TestProviderResult, Error, { provider: AiProviderConfig }>(
    async ({ provider }) => {
      const res = await adminApi.post<{ data: TestProviderResult }>(
        "/test-provider",
        { provider },
        { headers: headers(token) }
      );
      return res.data.data;
    },
    {
      onError: () => {
        toast.error("Falha ao testar provider");
      },
    }
  );
};
